import { default as React, useEffect, useState } from "react";

import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from "react-accessible-accordion";
import "react-accessible-accordion/dist/fancy-example.css";

import { gql } from "@apollo/client";
import ErrorScreen from "@components/error-pages/error-screen";
import { GenericExternalLink } from "@components/generic-external-link";
import { LoadingSlide } from "@components/title-slide-view";
import { H1 } from "@components/utilities";
import { LogOnMount } from "@components/v2/analytics";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchSpreadsheets_me_customInfluencerSheets,
  type FetchSpreadsheets_me_customSpendSheets,
  type FetchSpreadsheets,
} from "@north-beam/nb-client-side";

const AccordionPanel = ({ spreadSheetLink }: { spreadSheetLink: string }) => {
  return (
    <>
      <div className="row col-8">
        <div>
          View this spreadsheet{" "}
          <GenericExternalLink href={spreadSheetLink}>
            here.
          </GenericExternalLink>
        </div>
      </div>
      <div className="row">
        <div className="py-1 mb-5 col-12">
          <iframe
            src={spreadSheetLink}
            title="Spreadsheet"
            width="100%"
            height="500"
            frameBorder="0"
          />
        </div>
      </div>
    </>
  );
};

interface AccordionWrapperProps {
  title: string;
  spreadsheets:
    | FetchSpreadsheets_me_customInfluencerSheets[]
    | FetchSpreadsheets_me_customSpendSheets[];
}

const AccordionWrapper = ({ title, spreadsheets }: AccordionWrapperProps) => {
  return (
    <>
      <div className="container pt-4">
        <h3>
          <b>{title}</b>
        </h3>
        <hr />
        {spreadsheets && spreadsheets.length > 0 ? (
          <Accordion allowZeroExpanded>
            {spreadsheets.map((spreadsheet, index) => (
              <SpreadSheetAccordian
                key={index}
                index={index}
                spreadsheet={spreadsheet}
              />
            ))}
          </Accordion>
        ) : (
          <div className="col">
            It looks like you don&apos;t have any of these spreadsheets. Reach
            out to{" "}
            <a href="mailto:success@northbeam.io">our customer support team</a>{" "}
            if you would like to set one up.
          </div>
        )}
      </div>
    </>
  );
};

function SpreadSheetAccordian({
  index,
  spreadsheet,
}: {
  index: number;
  spreadsheet: FetchSpreadsheets_me_customInfluencerSheets;
}) {
  return (
    <AccordionItem uuid={index} key={`item-${index}`}>
      <AccordionItemHeading>
        <AccordionItemButton className="bg-white rounded-md border p-4">
          {spreadsheet.title
            ? spreadsheet.title
            : `Spreadsheet ${index + 1}: Cannot infer name`}
        </AccordionItemButton>
      </AccordionItemHeading>
      <AccordionItemPanel>
        <AccordionPanel spreadSheetLink={spreadsheet.url} />
      </AccordionItemPanel>
    </AccordionItem>
  );
}

export const DataImportSpreadsheets = () => {
  const { data, loading, error } = useNorthbeamQuery<FetchSpreadsheets>(
    FETCH_SPREADSHEETS,
    {},
  );

  if (loading || !data) {
    return <LoadingSlide />;
  }

  if (error) {
    return <ErrorScreen />;
  }

  return (
    <div className="container pt-4">
      <LogOnMount name="Visit Data Import Spreadsheets Page" />
      <H1>Data Import Spreadsheets</H1>
      <AccordionWrapper
        title="Spend spreadsheets"
        spreadsheets={data.me.customSpendSheets}
      />
      <br />
      <AccordionWrapper
        title="Influencer spreadsheets"
        spreadsheets={data.me.customInfluencerSheets}
      />
      <br />
    </div>
  );
};

export const FETCH_SPREADSHEETS = gql`
  query FetchSpreadsheets {
    me {
      customSpendSheets {
        url
        title
      }
      customInfluencerSheets {
        url
        title
      }
    }
  }
`;
